import * as THREE from 'three';
import type { ActiveSelection } from '../activeSelection';
import { SelectableObject } from '../selectableObject';
import type { SelectMarqueeTool } from '../tools/selectMarqueeTool';
import type {
  PointerDownResult,
  ScenePointerContext,
  ScenePointerInputHandler,
} from './scenePointerInputHandler';
import { deselectAll } from '@/stores/selectionStore';

export class SceneSelectionInputHandler implements ScenePointerInputHandler {
  constructor(
    private readonly activeSelection: ActiveSelection,
    private readonly selectMarqueeTool: SelectMarqueeTool,
  ) {}

  attach() {
    this.selectMarqueeTool.attach();
  }

  detach() {
    this.selectMarqueeTool.detach();
  }

  onPointerDown(context: ScenePointerContext): PointerDownResult {
    if (context.activeTool === 'place') {
      return 'ignored';
    }

    const dragHits = context.raycaster.intersectObjects(
      this.activeSelection.getPickTargets(),
      false,
    );

    const dragHit = dragHits.reduce<THREE.Intersection | null>((best, hit) => {
      const hitPriority =
        (hit.object.userData.dragPriority as number | undefined) ?? 0;
      const bestPriority = best
        ? ((best.object.userData.dragPriority as number | undefined) ?? 0)
        : -Infinity;

      if (!best || hitPriority > bestPriority) {
        return hit;
      }

      return best;
    }, null);

    if (
      dragHit &&
      this.activeSelection.startDrag(
        dragHit,
        context.raycaster.ray,
        context.camera,
      )
    ) {
      return 'captured';
    }

    if (context.activeTool === 'select' && dragHits.length > 0) {
      return 'handled';
    }

    if (!context.canSelectObjects) {
      deselectAll();
      return 'handled';
    }

    let closestHit: THREE.Intersection | null = null;
    let closestSceneObject: SelectableObject | null = null;

    for (const sceneObject of context.sceneObjects) {
      if (sceneObject === this.activeSelection) {
        continue;
      }

      if (!(sceneObject instanceof SelectableObject)) {
        continue;
      }

      const intersections = context.raycaster.intersectObjects(
        sceneObject.getPickTargets(),
        false,
      );
      const hit = intersections[0];
      if (!hit) {
        continue;
      }

      if (closestHit === null || hit.distance < closestHit.distance) {
        closestHit = hit;
        closestSceneObject = sceneObject;
      }
    }

    if (closestHit && closestSceneObject) {
      closestSceneObject.onClick(closestHit, context.event);
      return 'handled';
    }

    if (context.activeTool === 'select') {
      this.selectMarqueeTool.startDrag(
        context.event.clientX,
        context.event.clientY,
      );
      return 'captured';
    }

    deselectAll();
    return 'handled';
  }

  onPointerMove(context: ScenePointerContext): boolean {
    if (this.selectMarqueeTool.isDragging()) {
      this.selectMarqueeTool.updateDrag(
        context.event.clientX,
        context.event.clientY,
      );
      return true;
    }

    if (!this.activeSelection.isDragging()) {
      return false;
    }

    this.activeSelection.updateDrag(
      context.raycaster.ray,
      context.event.shiftKey,
    );
    return true;
  }

  onPointerUp(context: ScenePointerContext): boolean {
    if (this.selectMarqueeTool.isDragging()) {
      const selectionRect = this.selectMarqueeTool.finishDrag();
      if (selectionRect) {
        context.onSelectMarqueeRect(selectionRect);
      }
      return true;
    }

    if (!this.activeSelection.isDragging()) {
      return false;
    }

    this.activeSelection.endDrag();
    return true;
  }

  isDragging() {
    return (
      this.selectMarqueeTool.isDragging() || this.activeSelection.isDragging()
    );
  }

  cancel() {
    if (this.selectMarqueeTool.isDragging()) {
      this.selectMarqueeTool.cancelDrag();
    }

    if (this.activeSelection.isDragging()) {
      this.activeSelection.endDrag();
    }
  }
}
